import { createAsyncThunk, isFulfilled, isPending } from '@reduxjs/toolkit';
import axios from 'axios';

import { ISensorTruckInfo } from 'app/modules/dashboards/dashboard-truck-implement/interface/dashboardTruckImplementInterface';
import { PeriodicType } from 'app/shared/model/enumerations/periodicType';
import { ISensor, defaultValue } from 'app/shared/model/sensor.model';
import { EntityState, IQueryParams, createEntitySlice, serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { cleanEntity } from 'app/shared/util/entity-utils';

const initialState: EntityState<ISensor> = {
  loading: false,
  errorMessage: null,
  entities: [],
  entitiesAlt: [] as ISensorTruckInfo[],
  entitiesAlt2: null as string | null,
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
};

const apiUrl = 'api/sensors';

// Actions

export const getEntities = createAsyncThunk('sensor/fetch_entity_list', async ({ page, size, sort }: IQueryParams) => {
  const requestUrl = `${apiUrl}${sort ? `?page=${page}&size=${size}&sort=${sort}&` : '?'}cacheBuster=${new Date().getTime()}`;
  return axios.get<ISensor[]>(requestUrl);
});

export const getOwnerSensorsWithoutPagination = createAsyncThunk('sensor/fetch_entity_list', async () => {
  const requestUrl = 'api/ownersensorswithoutpagination';
  return axios.get<ISensor[]>(requestUrl);
});

export const getUserSensorsWithoutPagination = createAsyncThunk('sensor/fetch_entity_list', async () => {
  const requestUrl = 'api/usersensorswithoutpagination';
  return axios.get<ISensor[]>(requestUrl);
});

export const getInstallerSensorsWithoutPagination = createAsyncThunk('sensor/fetch_entity_list', async () => {
  const requestUrl = 'api/installersensorswithoutpagination';
  return axios.get<ISensor[]>(requestUrl);
});

export const getSensorsByTruckAndPeriod = createAsyncThunk(
  'sensor/fetch_entity_list/truckAndPeriod',
  async ({ truckId, periodic }: { truckId: number; periodic: PeriodicType }) => {
    const requestUrl = `${apiUrl}/by-truck-and-period/${truckId}/${periodic}`;
    return await axios.get<ISensorTruckInfo[]>(requestUrl);
  },
  {
    serializeError: serializeAxiosError,
  }
);

export const getSensorsByTruckAndDateRange = createAsyncThunk(
  'sensor/fetch_entity_list/truckAndDateRange',
  async ({ truckId, dateStart, dateEnd }: { truckId: number; dateStart: string; dateEnd: string }) => {
    const requestUrl = `${apiUrl}/by-truck-and-date-range/${truckId}/${dateStart}/${dateEnd}`;
    return await axios.get<ISensorTruckInfo[]>(requestUrl);
  },
  {
    serializeError: serializeAxiosError,
  }
);

export const getEntity = createAsyncThunk(
  'sensor/fetch_entity',
  async (id: string | number) => {
    const requestUrl = `${apiUrl}/${id}`;
    return axios.get<ISensor>(requestUrl);
  },
  { serializeError: serializeAxiosError }
);

export const getEntityCluster = createAsyncThunk('sensor/fetch_entity_cluster', async (id: string | number) => {
  const requestUrl = `${apiUrl}/cluster/${id}`;
  return axios.get<string>(requestUrl);
});

export const createEntity = createAsyncThunk(
  'sensor/create_entity',
  async (entity: ISensor, thunkAPI) => {
    const result = await axios.post<ISensor>(apiUrl, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const updateEntity = createAsyncThunk(
  'sensor/update_entity',
  async (entity: ISensor, thunkAPI) => {
    const result = await axios.put<ISensor>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const partialUpdateEntity = createAsyncThunk(
  'sensor/partial_update_entity',
  async (entity: ISensor, thunkAPI) => {
    const result = await axios.patch<ISensor>(`${apiUrl}/${entity.id}`, cleanEntity(entity));
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

export const deleteEntity = createAsyncThunk(
  'sensor/delete_entity',
  async (id: string | number, thunkAPI) => {
    const requestUrl = `${apiUrl}/${id}`;
    const result = await axios.delete<ISensor>(requestUrl);
    thunkAPI.dispatch(getEntities({}));
    return result;
  },
  { serializeError: serializeAxiosError }
);

// slice

export const SensorSlice = createEntitySlice({
  name: 'sensor',
  initialState,
  extraReducers(builder) {
    builder
      .addCase(getEntity.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload.data;
      })

      .addCase(deleteEntity.fulfilled, state => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = {};
      })
      .addMatcher(isFulfilled(getEntities), (state, action) => {
        return {
          ...state,
          loading: false,
          entities: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getSensorsByTruckAndPeriod), (state, action) => {
        return {
          ...state,
          loading: false,
          entitiesAlt: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getSensorsByTruckAndDateRange), (state, action) => {
        return {
          ...state,
          loading: false,
          entitiesAlt: action.payload.data,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isFulfilled(getEntityCluster), (state, action) => {
        return {
          ...state,
          errorMessage: null,
          loading: false,
          entitiesAlt2: action.payload.data,
        };
      })
      .addMatcher(isPending(getEntityCluster), state => {
        state.errorMessage = null;
        state.loading = true;
        state.entitiesAlt2 = null; // Limpa o resultado anterior ao iniciar uma nova busca
      })
      .addMatcher(isFulfilled(createEntity, updateEntity, partialUpdateEntity), (state, action) => {
        state.updating = false;
        state.loading = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isPending(getEntities, getEntity, getSensorsByTruckAndPeriod, getSensorsByTruckAndDateRange), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loading = true;
      })
      .addMatcher(isPending(createEntity, updateEntity, partialUpdateEntity, deleteEntity), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      });
  },
});

export const { reset } = SensorSlice.actions;

// Reducer
export default SensorSlice.reducer;
